"use client";

import { useEffect, useLayoutEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { isGoogleAnalyticsEnabled } from "@/lib/analytics/google-env";
import { AW_ID, GA_ID } from "@/lib/analytics/google-ids";
import {
  readAdvertisingConsent,
  readAnalyticsConsent,
  readPersonalizationConsent,
} from "@/components/analytics/browser-attribution";
import {
  stripAdvertisingAttributionFromCurrentUrl,
  subscribeToBrowserConsent,
} from "@/components/analytics/browser-consent-lifecycle";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";
import { isGoogleTagSuppressedPath } from "@/lib/analytics/signed-url-surfaces";
import { setGoogleTagDestinationReady } from "@/lib/analytics/google-tag-readiness";

export { AW_ID, GA_ID };

export function googleAdsSendTo(label: string | undefined): string | null {
  if (!label) return null;
  return `${AW_ID}/${label}`;
}

function subscribeToAnalyticsEnvironment() {
  return () => {};
}

function getClientAnalyticsSnapshot() {
  return isGoogleAnalyticsEnabled();
}

function getServerAnalyticsSnapshot() {
  return isGoogleAnalyticsEnabled(process.env.NEXT_PUBLIC_APP_BASE_URL, null);
}

type GoogleConsentSnapshot = `${"a" | "-"}${"d" | "-"}${"p" | "-"}`;

function getClientConsentSnapshot(): GoogleConsentSnapshot {
  return `${readAnalyticsConsent() === "accepted" ? "a" : "-"}${
    readAdvertisingConsent() === "accepted" ? "d" : "-"
  }${readPersonalizationConsent() === "accepted" ? "p" : "-"}`;
}

function getServerConsentSnapshot(): GoogleConsentSnapshot {
  return "---";
}

function subscribeToConsent(callback: () => void) {
  if (typeof window === "undefined") return () => {};
  return subscribeToBrowserConsent(() => callback());
}

function ensureGtagShim(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
}

function ensureGoogleLibrary(loaderId: string): void {
  if (document.getElementById("gtag-lib")) return;
  const script = document.createElement("script");
  script.id = "gtag-lib";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(loaderId)}`;
  document.head.appendChild(script);
}

/**
 * Purpose-specific Google tag loader.
 *
 * The denied defaults remain inline in app/layout.tsx, but no Google network
 * resource or destination config is emitted until at least one corresponding
 * category has a valid v2 acceptance. Analytics and Ads destinations are
 * configured independently; ad personalisation is never granted.
 */
export function GoogleAnalytics() {
  const envEnabled = useSyncExternalStore(
    subscribeToAnalyticsEnvironment,
    getClientAnalyticsSnapshot,
    getServerAnalyticsSnapshot,
  );
  const consent = useSyncExternalStore(
    subscribeToConsent,
    getClientConsentSnapshot,
    getServerConsentSnapshot,
  );
  const pathname = usePathname();
  const suppressed = useAnalyticsSuppressed(pathname);
  // #2326 round 22: GA4 enhanced-measurement OUTBOUND CLICKS ship the full
  // link_url (presigned signature included) to Google, and enhanced
  // measurement has NO client-side off switch (data-stream admin config) —
  // so GA does not run on the surfaces that render signed URLs. The Google
  // tag's own exemption list keeps the /billing/success conversion surface
  // live (no presigned URLs render there; the Google Ads purchase
  // conversion has no server-side fallback) — see signed-url-surfaces.ts.
  const enabled =
    envEnabled && !suppressed && !isGoogleTagSuppressedPath(pathname);

  // #2326 round 23: the DENIAL must run PRE-PAINT. A passive effect fires
  // after the destination DOM (with its presigned hrefs) has mounted, so an
  // SPA navigation into a signed-URL surface would leave enhanced-measurement
  // click handling granted for the paint interval. Layout effects run before
  // the browser paints — the same boundary Clarity and PostHog use.
  useLayoutEffect(() => {
    if (typeof window === "undefined" || enabled) return;
    const win = window as unknown as Record<string, boolean>;
    win[`ga-disable-${GA_ID}`] = true;
    win[`ga-disable-${AW_ID}`] = true;
    setGoogleTagDestinationReady("analytics", false);
    setGoogleTagDestinationReady("advertising", false);
    window.gtag?.("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }, [enabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    ensureGtagShim();

    const analyticsAccepted = enabled && consent[0] === "a";
    const advertisingAccepted = enabled && consent[1] === "d";
    // Personalisation is separately chosen but cannot operate without the Ads
    // destination/storage. Keep this provider boundary fail-closed even before
    // CookieBanner migrates an older impossible cookie combination.
    const personalizationAccepted =
      advertisingAccepted && consent[2] === "p";
    // Invalidate readiness before queueing a denial. `window.gtag` remains
    // present as the all-denied shim and must not be treated as permission to
    // emit events while a destination is withdrawn or suppressed.
    if (!analyticsAccepted) {
      setGoogleTagDestinationReady("analytics", false);
    }
    if (!advertisingAccepted) {
      setGoogleTagDestinationReady("advertising", false);
    }
    if (analyticsAccepted && !advertisingAccepted) {
      stripAdvertisingAttributionFromCurrentUrl();
    }
    const win = window as unknown as Record<string, boolean>;
    win[`ga-disable-${GA_ID}`] = !analyticsAccepted;
    win[`ga-disable-${AW_ID}`] = !advertisingAccepted;

    window.gtag?.("consent", "update", {
      analytics_storage: analyticsAccepted ? "granted" : "denied",
      ad_storage: advertisingAccepted ? "granted" : "denied",
      ad_user_data: advertisingAccepted ? "granted" : "denied",
      ad_personalization: personalizationAccepted ? "granted" : "denied",
    });

    if (!analyticsAccepted && !advertisingAccepted) return;

    // Consent update must precede js/config in the queue. A later withdrawal
    // is enforced by both Consent Mode and the ga-disable destination flags.
    if (!document.documentElement.dataset.tsbGtagJsInitialised) {
      window.gtag?.("js", new Date());
      document.documentElement.dataset.tsbGtagJsInitialised = "true";
    }
    if (
      analyticsAccepted &&
      !document.documentElement.dataset.tsbGa4Configured
    ) {
      window.gtag?.("config", GA_ID, { anonymize_ip: true });
      document.documentElement.dataset.tsbGa4Configured = "true";
    }
    if (
      advertisingAccepted &&
      !document.documentElement.dataset.tsbGoogleAdsConfigured
    ) {
      window.gtag?.("config", AW_ID);
      document.documentElement.dataset.tsbGoogleAdsConfigured = "true";
    }
    ensureGoogleLibrary(advertisingAccepted ? AW_ID : GA_ID);

    // These transitions synchronously wake one-shot trackers. Keep them last:
    // their event commands must follow consent=granted and destination config
    // in dataLayer, including on a first-time Accept click.
    if (analyticsAccepted) {
      setGoogleTagDestinationReady("analytics", true);
    }
    if (advertisingAccepted) {
      setGoogleTagDestinationReady("advertising", true);
    }
  }, [consent, enabled]);

  return null;
}
